import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Key } from "lucide-react";

export default function LoginPage() {
  const { login, backdoorLogin, loading, error } = useAuth();
  const [loginMethod, setLoginMethod] = useState("redirect"); // "redirect" or "popup"
  const [showBackdoor, setShowBackdoor] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [backdoorError, setBackdoorError] = useState("");
  const [backdoorLoading, setBackdoorLoading] = useState(false);

  const handleLogin = () => {
    login(loginMethod === "popup");
  };

  const handleBackdoorSubmit = async (e) => {
    e.preventDefault();
    setBackdoorError("");
    setBackdoorLoading(true);
    
    const result = await backdoorLogin(username, password);
    
    if (!result.success) {
      setBackdoorError(result.error || "Invalid credentials");
    }
    setBackdoorLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      {/* Gamer Packaging branded accent bar */}
      <div className="gamer-accent-bar fixed top-0 left-0 right-0 z-[60]" />
      
      <Card className="w-full max-w-md" data-testid="login-page">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img 
              src="/images/gamer-logo.jpg" 
              alt="Gamer Packaging" 
              className="h-16"
            />
          </div>
          <CardTitle className="text-2xl font-bold">Project Management Dashboard</CardTitle>
          <CardDescription>
            Sign in with your Microsoft account to access your projects and tasks
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm text-center">
              {error}
              {error.includes("popup") || error.includes("redirect") ? null : (
                <button 
                  onClick={() => setLoginMethod(loginMethod === "redirect" ? "popup" : "redirect")}
                  className="block w-full mt-2 text-primary underline"
                >
                  Try {loginMethod === "redirect" ? "popup" : "redirect"} login instead
                </button>
              )}
            </div>
          )}
          
          <Button 
            onClick={handleLogin} 
            disabled={loading}
            className="w-full h-12 text-base"
            data-testid="microsoft-login-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <svg className="mr-2 h-5 w-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
                Sign in with Microsoft
              </>
            )}
          </Button>
          
          {/* Login method toggle */}
          <div className="flex justify-center gap-2 pt-2">
            <button
              onClick={() => setLoginMethod("redirect")}
              className={`text-xs px-3 py-1 rounded ${loginMethod === "redirect" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Redirect
            </button>
            <button
              onClick={() => setLoginMethod("popup")}
              className={`text-xs px-3 py-1 rounded ${loginMethod === "popup" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Popup
            </button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center pt-2">
            Only authorized Gamer Packaging employees can access this application
          </p>
          
          {/* Backdoor Admin Login */}
          <div className="pt-4 border-t">
            <button
              onClick={() => setShowBackdoor(true)}
              className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
              data-testid="backdoor-trigger"
            >
              <Key className="h-3 w-3" />
              Admin Login
            </button>
          </div>
        </CardContent>
      </Card>
      
      {/* Backdoor Login Dialog */}
      <Dialog open={showBackdoor} onOpenChange={setShowBackdoor}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Admin Login</DialogTitle>
            <DialogDescription>
              Enter admin credentials to bypass SSO
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBackdoorSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  data-testid="backdoor-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="backdoor-password"
                />
              </div>
              {backdoorError && (
                <p className="text-sm text-red-500" data-testid="backdoor-error">{backdoorError}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBackdoor(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={backdoorLoading} data-testid="backdoor-submit">
                {backdoorLoading ? "Signing in..." : "Sign In"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
